import React from "react";
import {Check, TimeSpan} from "@sparkware/app-utils";
import {ServiceBase} from "../ServiceBase";
import {makeObservable, observable, runInAction} from "mobx";
import {IModalDialogHandler} from "../dialog/dialog.service.interface";
import {CGPEventSubscription} from "../sdk/cgp-sdk.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {SessionCooldownPopupComponent} from "../../components/session-cooldown-popup/session-cooldown-popup.component";
import {ISessionCooldownService} from "./session-cooldown.service.interface";
import {NullableNumber} from "../../../common/types/nullable-types";
import {CyClass} from "../../../common/cy-classes";
import {namedSetInterval} from "../../../common/utils/time-functions";

export const COOL_DOWN_EXPIRATION_DATE_STORAGE_KEY: string = "orbit-session-cooldown-expiration-date";

export class SessionCooldownService extends ServiceBase implements ISessionCooldownService {

  private _sessionCoolDownConfig =  this.services.regulation.getSessionCooldownConfig();

  @observable.ref
  private _remainingTime: TimeSpan = TimeSpan.fromMilliseconds(0);

  private _dialogHandler: IModalDialogHandler | null = null;
  private _realityCheckOpenedEventHandler: CGPEventSubscription = null;
  private _realityCheckClosedEventHandler: CGPEventSubscription = null;

  constructor(services: IServiceFactoryExtended) {
    super(services);

    makeObservable(this);

    if (this._sessionCoolDownConfig.featureEnabled) {
      this.reaction(
        () => this.services.user.isAuthorized,
        isAuthorized => {
          if(isAuthorized) {
            this._onUserLogin();
          } else {
            this._onUserLogout()
          }
        },
        {
          fireImmediately: true
        }
      );
    }
  }

  private get coolDownExpirationDate(): NullableNumber {
    const storedExpirationDate = this.services.localStorage.getItem(COOL_DOWN_EXPIRATION_DATE_STORAGE_KEY);
    if(storedExpirationDate) {
       return parseInt(storedExpirationDate);
    }

    return null;
  }

  private set coolDownExpirationDate(value: NullableNumber) {
    if(Check.isNullOrUndefined(value)) {
      this.services.localStorage.removeItem(COOL_DOWN_EXPIRATION_DATE_STORAGE_KEY);
    } else {
      this.services.localStorage.setItem(COOL_DOWN_EXPIRATION_DATE_STORAGE_KEY, value);
    }
  }

  private get isInCoolDown(): boolean {
    const expirationDate = this.coolDownExpirationDate;
    if(Check.isNullOrUndefined(expirationDate)) {
      return false;
    }

    return this.services.time.now() < expirationDate;
  }

  private _onUserLogin() {
    if(this.isInCoolDown) {
      this._showCoolDownDialog();
    }

    this._realityCheckClosedEventHandler = this.services.sdk.events.onRealityCheckClosed(() => {
      this.coolDownExpirationDate = this.services.time.now() + this._sessionCoolDownConfig.cooldownTime.totalMilliseconds;
      this._showCoolDownDialog();
    });

    this._realityCheckOpenedEventHandler = this.services.sdk.events.onRealityCheckOpened(() => {
      this._closeCoolDownDialog();
      this.coolDownExpirationDate = null;
    });
  }

  private _onUserLogout() {

    this._closeCoolDownDialog();

    this._realityCheckClosedEventHandler?.off();
    this._realityCheckClosedEventHandler = null;

    this._realityCheckOpenedEventHandler?.off();
    this._realityCheckOpenedEventHandler = null;

    this.coolDownExpirationDate = null;
  }

  private _updateRemainingTime(): void {
    runInAction(() => {
      if(this.isInCoolDown) {
        this._remainingTime = TimeSpan.fromMilliseconds(this.coolDownExpirationDate - this.services.time.now());
      } else {
        this.coolDownExpirationDate = null;
        this._remainingTime = TimeSpan.fromMilliseconds(0);
      }
    });
  }

  private _countDownTimerRef: any = null;
  private _startCountDownTimer(): void {
    this._updateRemainingTime();
    this._countDownTimerRef = namedSetInterval('COOL_DOWN_COUNT_DOWN_TIMER',() => {
      this._updateRemainingTime();

      if(this.isInCoolDown) {
        this._preventCoolDownByPass(); //this is for the case when a smart user removes the dialog from DOM using dev tools
      } else {
        this._closeCoolDownDialog();
      }
    }, 1000);
  }

  private _stopCountDownTimer(): void {
    if(this._countDownTimerRef) {
      clearInterval(this._countDownTimerRef);
      this._countDownTimerRef = null;
    }
  }

  private _showCoolDownDialog() {

    this.services.dialog.showModal((dialogHandler) => {
      this._dialogHandler = dialogHandler;
      return <SessionCooldownPopupComponent getRemainingTime={() => this._remainingTime} />
    }, {
      title: this.services.translation.translate("SESSION_COOLDOWN_POPUP_TITLE"),
      isHighPriority: true,
      hideCloseButton: true,
      shouldBeClosedOnlyByUser: true
    });

    this.services.analytics.sendElasticInfoEvent({
      eventType:"Lugas",
      category:"SESSION_COOLDOWN_POPUP",
      action:"SHOW_POPUP"
    }, false);

    this._startCountDownTimer();
  }

  private _closeCoolDownDialog(): void {
    this._stopCountDownTimer();
    if(this._dialogHandler) {
      this._dialogHandler.close();
      this._dialogHandler = null;
      this.services.analytics.sendElasticInfoEvent({
        eventType:"Lugas",
        category:"SESSION_COOLDOWN_POPUP",
        action:"HIDE_POPUP"
      }, false);

    }

  }

  private _preventCoolDownByPass(): void {
    const elem = document.getElementsByClassName(CyClass('cy-session-cooldown-popup'));
    if (elem.length === 0) {
      this.services.analytics.sendElasticInfoEvent({
        eventType:"Lugas",
        category:"SESSION_COOLDOWN_POPUP",
        action:"POPUP_MANIPULATED"
      }, false);
      this._stopCountDownTimer();
      this.services.window.reload();
    }
  }


}
